import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { CalendarDots, CreditCard, CheckCircle, ArrowLeft, Phone, Copy, UploadSimple, IdentificationBadge } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import LanguageToggle from '../components/LanguageToggle';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ZELLE_PHONE = "5618596830";

export default function OpenPlayPublic() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const isSuccess = searchParams.get('success') === 'true';

  const [step, setStep] = useState(isSuccess ? 'success' : 'form');

  useEffect(() => {
    const sid = searchParams.get('session_id');
    if (!isSuccess || !sid) return;
    let n = 0;
    const chk = async () => {
      n += 1;
      try {
        const { data } = await axios.get(`${API}/open-play/checkout-status/${sid}`);
        if (data.payment_status !== 'paid' && n < 4) setTimeout(chk, 1500);
      } catch { if (n < 4) setTimeout(chk, 1500); }
    };
    chk();
  }, [isSuccess, searchParams]);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', preferred_date: '', notes: '' });
  const [isStudent, setIsStudent] = useState(false);
  const [checkingStudent, setCheckingStudent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [regId, setRegId] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  // Auto-detect if the user is an active TK student based on email/phone (debounced)
  useEffect(() => {
    const phone = (formData.phone || '').replace(/\D/g, '');
    const email = (formData.email || '').trim();
    if (phone.length < 7 && !email) {
      setIsStudent(false);
      return;
    }
    const timer = setTimeout(async () => {
      setCheckingStudent(true);
      try {
        const params = new URLSearchParams();
        if (phone) params.set('phone', phone);
        if (email) params.set('email', email);
        const { data } = await axios.get(`${API}/open-play/price?${params.toString()}`);
        setIsStudent(!!data.is_student);
      } catch {
        setIsStudent(false);
      } finally {
        setCheckingStudent(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [formData.phone, formData.email]);

  const amount = isStudent ? 12 : 15;
  const amountCard = (amount * 1.05).toFixed(2);

  const isEn = i18n.language === 'en';
  const T = {
    title: isEn ? 'Open Play' : 'Open Play',
    subtitle: isEn ? 'Come and play — no schedule needed' : 'Venha jogar — sem hora marcada',
    student: isEn ? 'TK Student' : 'Aluno(a) TK',
    nonStudent: isEn ? 'Visitor' : 'Visitante',
    detectedStudent: isEn ? 'We recognized you as an active TK student — you pay only $12!' : 'Detectamos que voce e aluno(a) TK ativo(a) — voce paga so $12!',
    autoPricing: isEn ? 'Price adjusts automatically after you fill your email/phone' : 'O preco ajusta automaticamente ao preencher seu email/telefone',
    continue: isEn ? 'Continue to payment' : 'Continuar para pagamento',
    choosePayment: isEn ? 'Choose your payment method' : 'Escolha a forma de pagamento',
    zelleTitle: isEn ? 'Pay via Zelle' : 'Pagar via Zelle',
    zelleSubtitle: (amt) => isEn ? `Send ${amt} to the number below` : `Envie ${amt} para o numero abaixo`,
    zelleNumber: isEn ? 'Zelle number' : 'Numero Zelle',
    zelleNote: isEn ? 'After sending the payment, upload the receipt below to confirm your slot.' : 'Depois de enviar o pagamento, faca upload do comprovante abaixo para confirmar.',
    confirmedTitle: isEn ? 'Registration confirmed!' : 'Inscricao confirmada!',
    confirmedSubtitle: isEn ? "See you at the court. We'll send you a confirmation email." : 'A gente se ve na quadra. Voce recebera um email de confirmacao.',
    preferredDate: isEn ? 'Preferred date' : 'Data desejada',
    notesPh: isEn ? 'Any notes (optional)' : 'Alguma observacao (opcional)',
  };

  const handleSubmitForm = (e) => {
    e.preventDefault();
    setStep('payment');
  };

  const handlePayWithCard = async () => {
    setLoading(true);
    try {
      const { data: regData } = await axios.post(`${API}/open-play/register`, formData);
      const { data: checkoutData } = await axios.post(`${API}/open-play/checkout`, {
        registration_id: regData.id,
        origin_url: window.location.origin,
      });
      if (checkoutData.url) {
        setCheckoutUrl(checkoutData.url);
        setStep('redirecting');
        setTimeout(() => { window.location.assign(checkoutData.url); }, 800);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEn ? 'Error processing' : 'Erro ao processar'));
      setLoading(false);
    }
  };

  const handlePayWithZelle = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/open-play/register`, {
        ...formData,
        notes: (formData.notes ? formData.notes + ' | ' : '') + 'Pagamento via Zelle',
      });
      setRegId(data.id);
      setStep('zelle-info');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro');
    } finally {
      setLoading(false);
    }
  };

  const handleReceiptUpload = async (file) => {
    if (!file || !regId) return;
    setUploadingReceipt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/open-play/zelle-receipt?registration_id=${regId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(isEn ? 'Receipt sent! Payment confirmed.' : 'Comprovante enviado! Pagamento confirmado.');
      setStep('success');
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEn ? 'Upload error' : 'Erro ao enviar comprovante'));
    } finally {
      setUploadingReceipt(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="bg-white border-b border-[#E6E2D8] px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-tk.png" alt="Logo" className="w-8 h-8 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
            <span className="font-['Outfit'] font-bold text-[#1A1A1A]">Tia Karol Sports</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Link to="/login" className="text-sm text-[#E85D04] hover:text-[#DC2F02] font-medium">
              {t('common.doLogin')}
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#73706A] hover:text-[#1A1A1A] mb-6">
          <ArrowLeft size={16} /> {t('common.back')}
        </Link>

        {step === 'success' && (
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 text-center" data-testid="openplay-success">
            <div className="w-20 h-20 bg-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-white" weight="fill" />
            </div>
            <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A] mb-3">{T.confirmedTitle}</h2>
            <p className="text-[#73706A] mb-6">{T.confirmedSubtitle}</p>
            <Link to="/login">
              <Button className="bg-[#E85D04] hover:bg-[#DC2F02] text-white">{t('common.doLogin')}</Button>
            </Link>
          </div>
        )}

        {step === 'form' && (
          <div className="bg-white border border-[#E6E2D8] rounded-xl overflow-hidden">
            <div className="h-48 bg-cover bg-center relative" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1596489396342-9988b49dbbba?q=80&w=2832&auto=format&fit=crop)' }}>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20" />
              <div className="absolute bottom-0 left-0 p-6 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <CalendarDots size={28} className="text-[#FFB703]" weight="fill" />
                  <h1 className="font-['Outfit'] text-3xl font-bold">{T.title}</h1>
                </div>
                <p className="text-white/80">{T.subtitle}</p>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-5 bg-[#FFF3E6] border-l-4 border-[#E85D04] rounded-r-lg p-3" data-testid="openplay-student-notice">
                <p className="text-sm text-[#1A1A1A] font-semibold">É aluno(a) TK? Não use esta página.</p>
                <p className="text-xs text-[#73706A] mt-0.5">Acesse o <a href="/login" className="text-[#E85D04] font-semibold underline">Portal do Aluno</a> e pague o Open Play por lá. Esta página é para visitantes.</p>
              </div>
              <form onSubmit={handleSubmitForm} className="space-y-5">
                <div>
                  <Label>{t('common.fullName')} *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder={t('common.name')} required data-testid="openplay-name" />
                </div>
                <div>
                  <Label>{t('common.phone')} *</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="(000) 000-0000" required data-testid="openplay-phone" />
                </div>
                <div>
                  <Label>{t('common.email')} ({t('common.optional')})</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="voce@email.com" data-testid="openplay-email" />
                </div>
                <div>
                  <Label>{T.preferredDate} *</Label>
                  <Input type="date" value={formData.preferred_date} onChange={(e) => setFormData({ ...formData, preferred_date: e.target.value })} required data-testid="openplay-date" />
                </div>
                <div>
                  <Label>{t('common.notes')}</Label>
                  <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="input-field min-h-[60px] resize-none" placeholder={T.notesPh} data-testid="openplay-notes" />
                </div>

                {isStudent && (
                  <div className="p-3 bg-[#E9F5EF] border-2 border-[#2D6A4F] rounded-lg flex items-start gap-2" data-testid="openplay-student-badge">
                    <IdentificationBadge size={22} className="text-[#2D6A4F] flex-shrink-0 mt-0.5" weight="fill" />
                    <p className="text-sm text-[#1A1A1A]"><strong className="text-[#2D6A4F]">{T.student}</strong> — {T.detectedStudent}</p>
                  </div>
                )}

                <div className="p-4 bg-[#F5F2EB] rounded-lg text-center">
                  <p className="text-sm text-[#73706A]">{isStudent ? T.student : T.nonStudent}</p>
                  <p className="text-3xl font-bold text-[#E85D04] font-['Outfit'] mt-1" data-testid="openplay-amount-display">${amount.toFixed(2)}</p>
                  <p className="text-xs text-[#73706A] mt-1">{t('common.card')}: ${amountCard} | Zelle: ${amount.toFixed(2)}</p>
                  <p className="text-[11px] text-[#73706A] mt-2 italic">{T.autoPricing}</p>
                </div>

                <Button type="submit" className="w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white py-3 text-base" data-testid="openplay-continue" disabled={checkingStudent}>
                  {T.continue}
                </Button>
              </form>
            </div>
          </div>
        )}

        {step === 'redirecting' && (
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-8 text-center space-y-6" data-testid="openplay-redirecting">
            <div className="w-16 h-16 rounded-full border-4 border-[#E85D04] border-t-transparent animate-spin mx-auto" />
            <div>
              <h2 className="font-['Outfit'] text-xl font-bold text-[#1A1A1A]">
                {isEn ? 'Opening secure checkout...' : 'Abrindo pagamento seguro...'}
              </h2>
              <p className="text-sm text-[#73706A] mt-2">
                {isEn ? "If the page doesn't open automatically, tap the button below." : 'Se a pagina nao abrir automaticamente, toque no botao abaixo.'}
              </p>
            </div>
            {checkoutUrl && (
              <a
                href={checkoutUrl}
                target="_self"
                rel="noopener"
                className="inline-flex items-center justify-center w-full bg-[#E85D04] hover:bg-[#DC2F02] text-white font-semibold py-4 rounded-lg text-lg transition-colors"
                data-testid="openplay-manual-checkout"
              >
                {isEn ? 'Go to payment' : 'Ir para o pagamento'} <ArrowLeft className="rotate-180 ml-2" size={20} />
              </a>
            )}
            {checkoutUrl && (
              <button
                onClick={() => { navigator.clipboard?.writeText(checkoutUrl); toast.success(isEn ? 'Link copied' : 'Link copiado'); }}
                className="text-sm text-[#0077B6] underline"
                data-testid="openplay-copy-checkout"
              >
                {isEn ? 'Copy payment link' : 'Copiar link do pagamento'}
              </button>
            )}
            <p className="text-xs text-[#73706A] max-w-sm mx-auto">
              {isEn
                ? 'Using Instagram/Facebook? Open this site in Safari or Chrome to pay.'
                : 'Está pelo Instagram/Facebook? Abra este site no Safari ou Chrome para pagar.'}
            </p>
            <button onClick={() => { setStep('payment'); setLoading(false); setCheckoutUrl(null); }} className="text-sm text-[#73706A] hover:text-[#1A1A1A]">
              {t('common.back')}
            </button>
          </div>
        )}

        {step === 'payment' && (
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-6 space-y-6">
            <div className="text-center">
              <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">{T.choosePayment}</h2>
              <p className="text-[#73706A] mt-1">{formData.name} - {formData.preferred_date}</p>
              {isStudent && (
                <p className="text-xs text-[#2D6A4F] font-semibold mt-2 flex items-center justify-center gap-1">
                  <IdentificationBadge size={14} weight="fill" /> {T.student} — $12
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <button onClick={handlePayWithCard} disabled={loading} className="p-6 border-2 border-[#E6E2D8] rounded-xl hover:border-[#E85D04] transition-all text-center group" data-testid="openplay-pay-card">
                <div className="w-16 h-16 bg-[#E85D04] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <CreditCard size={32} className="text-white" />
                </div>
                <h3 className="font-semibold text-lg text-[#1A1A1A]">{t('common.card')}</h3>
                <p className="text-xl font-bold text-[#E85D04] font-['Outfit'] mt-1">${amountCard}</p>
                <p className="text-xs text-[#73706A]">${amount.toFixed(2)} + 5%</p>
                {loading && <div className="mt-3 flex justify-center"><div className="w-5 h-5 border-2 border-[#E85D04] border-t-transparent rounded-full animate-spin" /></div>}
              </button>
              <button onClick={handlePayWithZelle} disabled={loading} className="p-6 border-2 border-[#E6E2D8] rounded-xl hover:border-[#0077B6] transition-all text-center group" data-testid="openplay-pay-zelle">
                <div className="w-16 h-16 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <Phone size={32} className="text-white" />
                </div>
                <h3 className="font-semibold text-lg text-[#1A1A1A]">Zelle</h3>
                <p className="text-xl font-bold text-[#0077B6] font-['Outfit'] mt-1">${amount.toFixed(2)}</p>
                <p className="text-xs text-[#73706A]">{t('common.noFee')}</p>
              </button>
            </div>
            <button onClick={() => setStep('form')} className="text-sm text-[#73706A] hover:text-[#1A1A1A] flex items-center gap-1 mx-auto">
              <ArrowLeft size={14} /> {t('common.back')}
            </button>
          </div>
        )}

        {step === 'zelle-info' && (
          <div className="bg-white border border-[#E6E2D8] rounded-xl p-6 text-center space-y-6">
            <div className="w-20 h-20 bg-[#0077B6] rounded-full flex items-center justify-center mx-auto">
              <Phone size={40} className="text-white" />
            </div>
            <div>
              <h2 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A]">{T.zelleTitle}</h2>
              <p className="text-[#73706A] mt-2">{T.zelleSubtitle(`$${amount.toFixed(2)}`)}</p>
            </div>
            <div className="bg-[#F5F2EB] rounded-lg p-6">
              <p className="text-sm text-[#73706A] mb-2">{T.zelleNumber}</p>
              <div className="flex items-center justify-center gap-3">
                <p className="text-3xl font-bold text-[#1A1A1A] font-['Outfit'] tracking-wider">{ZELLE_PHONE}</p>
                <button onClick={() => { navigator.clipboard.writeText(ZELLE_PHONE); toast.success(t('common.copied')); }} className="p-2 rounded-lg hover:bg-[#E6E2D8]">
                  <Copy size={20} className="text-[#0077B6]" />
                </button>
              </div>
              <p className="text-sm text-[#73706A] mt-3">Tia Karol Sports</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
              <p className="text-sm text-yellow-800">{T.zelleNote}</p>
            </div>
            <label className="cursor-pointer w-full">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={(e) => handleReceiptUpload(e.target.files[0])}
                data-testid="openplay-zelle-upload"
              />
              <Button className="w-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white pointer-events-none" disabled={uploadingReceipt} data-testid="openplay-upload-btn">
                {uploadingReceipt ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                ) : (
                  <UploadSimple size={20} className="mr-2" />
                )}
                {uploadingReceipt ? (isEn ? 'Sending...' : 'Enviando...') : t('common.uploadReceipt')}
              </Button>
            </label>
            <Link to="/login"><Button variant="outline" className="w-full">{t('common.doLogin')}</Button></Link>
          </div>
        )}
      </main>
    </div>
  );
}
